import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import type { CreditNoteCharge, CreateCreditNoteResponse, UpdateCreditNoteRequest } from '@/api/billing/credit-note-schemas'
import {
  getCreditNoteById,
  createCreditNote,
  updateCreditNote,
  deleteCreditNoteItemById,
  getAssetDetails,
  getAssetVat,
  approveCreditNoteById,
  regenerateCreditNote,
  recreateCreditNote,
  cancelCreditNoteById,
  sendCreditNoteById,
} from '@/api/billing/credit-notes'
import { queryKeys } from '@/features/credit-notes/queries'
import { InvoiceStatus } from '@/api/billing/constants'
import { updateInvoiceInListedInvoiceQueryCache } from './invoices'

export function useCreditNoteDraftSeed(assetCode: MaybeRefOrGetter<string | null | undefined>) {
  const enabled = computed(() => {
    const code = toValue(assetCode)
    return !!code && code.trim().length > 0
  })

  const assetQuery = useQuery({
    queryKey: computed(() => ['asset-details', toValue(assetCode)]),
    enabled,
    queryFn: () => getAssetDetails(toValue(assetCode)!)
  })

  const vatQuery = useQuery({
    queryKey: computed(() => ['asset-vat', toValue(assetCode)]),
    enabled,
    queryFn: () => getAssetVat(toValue(assetCode)!)
  })

  const vatPercentage = computed(() => vatQuery.data.value?.ratePercentage ?? 0)

  return {
    assetQuery,
    vatQuery,
    vatPercentage,
    isLoading: computed(() => assetQuery.isFetching.value || vatQuery.isFetching.value),
    isError: computed(() => assetQuery.isError.value || vatQuery.isError.value),
  }
}

function normaliseId(id: MaybeRefOrGetter<number>) {
  return computed(() => {
    const v = Number(toValue(id))
    return Number.isFinite(v) ? v : 0
  })
}

function patchStatus(qc: ReturnType<typeof useQueryClient>, id: number, status: string) {
  qc.setQueryData<CreateCreditNoteResponse | undefined>(queryKeys.creditNotes.detail(id), prev => {
    if (!prev) return prev
    return { ...prev, status }
  })
}

export function useGetCreditNote(id: MaybeRefOrGetter<number>) {
  const noteId = normaliseId(id)
  const enabled = computed(() => noteId.value > 0)

  return useQuery({
    queryKey: computed(() => queryKeys.creditNotes.detail(noteId)),
    enabled,
    queryFn: () => getCreditNoteById(noteId.value),
    placeholderData: prev => prev
  })
}

export function useCreateCreditNote() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (payload: { assetCode: string; charges: CreditNoteCharge[]; notes?: string }) => {
      const assetCode = String(payload.assetCode ?? '').trim()
      if (!assetCode) throw new Error('Invalid asset code')

      return createCreditNote({
        assetCode,
        notes: payload.notes ?? '',
        items: payload.charges.map(c => ({
          description: c.description,
          tariffInCents: Math.round((c.tariffPerKWh ?? 0) * 100),
          quantityInKWh: c.kWh ?? 0
        }))
      })
    },
    onSuccess: created => {
      qc.setQueryData(queryKeys.creditNotes.detail(created.id), created)
      qc.invalidateQueries({ queryKey: queryKeys.creditNotes.all() })
    }
  })
}

export function useUpdateCreditNoteLines(id: MaybeRefOrGetter<number>) {
  const qc = useQueryClient()
  const noteId = normaliseId(id)

  return useMutation({
    mutationFn: (payload: { charges: CreditNoteCharge[]; vatPercentage: number; notes?: string }) => {
      if (noteId.value <= 0) throw new Error('Invalid credit note id')

      const req: UpdateCreditNoteRequest = {
        id: noteId.value,
        notes: payload.notes ?? '',
        creditNoteItems: payload.charges.map(c => ({
          id: c.id ?? 0,
          description: c.description,
          tariffInCents: (c.tariffPerKWh ?? 0) * 100,
          quantityInKWh: c.kWh ?? 0
        }))
      }

      return updateCreditNote(req)
    },
    onSuccess: updated => {
      qc.setQueryData(queryKeys.creditNotes.detail(noteId.value), updated)
      qc.invalidateQueries({ queryKey: queryKeys.creditNotes.all() })
    }
  })
}

export function useDeleteCreditNoteLine(id: MaybeRefOrGetter<number>) {
  const qc = useQueryClient()
  const noteId = normaliseId(id)

  return useMutation({
    mutationFn: (lineId: number) => {
      if (noteId.value <= 0) throw new Error('Invalid credit note id')
      return deleteCreditNoteItemById(noteId.value, lineId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.creditNotes.detail(noteId.value) })
      qc.invalidateQueries({ queryKey: queryKeys.creditNotes.all() })
    }
  })
}

export function useApproveCreditNote() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => approveCreditNoteById(id),
    onSuccess: (approved, id) => {
      qc.setQueryData(queryKeys.creditNotes.detail(id), approved)
      qc.invalidateQueries({ queryKey: queryKeys.creditNotes.all() })
    }
  })
}

export function useSendCreditNote() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => sendCreditNoteById(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices:list'] }).catch(() => { })
    }
  })
}

export function useCancelCreditNote() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => cancelCreditNoteById(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices:list'] }).catch(() => { })
    }
  })
}

// export function useCreateCreditNote() {
//   return useMutation<CreateCreditNoteResponse, unknown, CreateCreditNoteRequest>({
//     mutationFn: payload => createCreditNote(payload)
//   })
// }

export function useApproveCreditNoteFromDashboard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (creditNoteId: MaybeRefOrGetter<number>) => approveCreditNoteById(toValue(creditNoteId)),
    async onSuccess(data, variables) {
      updateInvoiceInListedInvoiceQueryCache(
        queryClient,
        variables,
        cn => ({ ...cn, status: InvoiceStatus.Approved })
      )

        await Promise.allSettled([
          queryClient.invalidateQueries({ queryKey: ['invoices', variables] }),
          queryClient.invalidateQueries({ queryKey: ['credit-notes', variables] }),
          queryClient.invalidateQueries({ queryKey: ['credit-notes:activity', variables] }),
        ])
    }
  })
}

export function useRegenerateCreditNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (creditNoteId: MaybeRefOrGetter<number>) => {
      return regenerateCreditNote(toValue(creditNoteId))
    }, onSuccess() {
      qc.invalidateQueries({ queryKey: ['invoices:list'] }).catch(() => { })
    }
  })
}

export function useReCreateCreditNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (creditNoteId: MaybeRefOrGetter<number>) => {
      return recreateCreditNote(toValue(creditNoteId))
    }, onSuccess() {
      qc.invalidateQueries({ queryKey: ['invoices:list'] }).catch(() => { })
    }
  })
}